import { useActorMember, useAuth } from '@healthblocks-io/core'
import isAfter from 'date-fns/isAfter'
import { useCallback, useEffect, useState } from 'react'

import { useAppState } from '@/capacitor/hooks/useAppState'
import { mixpanel } from '@/mixpanel'
import { SESSION_DURATION } from '@/modules/core/constants/idle-timer.constants'
import { useDebugSettings } from '@/modules/profile/hooks/useDebugSettings'
import { isProduction } from '@/utils/environment'
import { StorageKeys, StorageService } from '@/utils/storageService'

/**
 * Identify the Mixpanel user based on the actor member id
 */
export const identifyMixpanelUser = (actorMemberId?: number) => {
  if (actorMemberId) {
    mixpanel.identify(actorMemberId.toString())
  }
}

/**
 * Hook to check if the session is expired
 */
export const useAuthenticedSession = () => {
  // -- State --
  const [lastLoggedInAt, setLastLoggedInAt] = useState<number>()
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  // -- Hooks --
  const auth = useAuth()
  const actorMember = useActorMember()
  const { debugSettings } = useDebugSettings()
  const sessionDuration = isProduction()
    ? // default 3 months
      SESSION_DURATION
    : debugSettings?.sessionDuration || SESSION_DURATION

  const { active } = useAppState()

  /**
   * Check if the session is expired, based on the last logged in time
   */
  const checkIfSessionIsExpired = useCallback(async (): Promise<boolean> => {
    if (!auth.data?.access_token) {
      setIsSessionExpired(false)
      return false
    }

    const lastLoggedInString = await StorageService.getItem(StorageKeys.LastLoggedInAt)
    // convert lastLoggedInString to a number
    const lastLoggedIn = lastLoggedInString ? Number.parseInt(lastLoggedInString) : undefined

    setLastLoggedInAt(lastLoggedIn)

    // if there is no last logged in time yet (first time login)
    if (!lastLoggedIn) {
      setIsSessionExpired(false)

      return false
    }

    const sessionExpiresAt = lastLoggedIn + sessionDuration
    const isExpired = isAfter(Date.now(), sessionExpiresAt)

    setIsSessionExpired(isExpired)

    return isExpired
  }, [auth.data?.access_token, sessionDuration])

  const updateLastLoggedIn = useCallback(async () => {
    const now = Date.now()
    await StorageService.setItem(StorageKeys.LastLoggedInAt, now)
    setLastLoggedInAt(now)
  }, [])

  // -- Effects --
  useEffect(() => {
    if (active) {
      checkIfSessionIsExpired()
    }
  }, [active, sessionDuration, checkIfSessionIsExpired])

  useEffect(() => {
    const fetchLastLoggedInFromStorage = async () => {
      const lastLoggedInString = await StorageService.getItem(StorageKeys.LastLoggedInAt)
      const lastLoggedIn = lastLoggedInString ? Number.parseInt(lastLoggedInString) : undefined

      setLastLoggedInAt(lastLoggedIn)
    }
    fetchLastLoggedInFromStorage()
  }, [])

  // Effect to identify user in Mixpanel
  useEffect(() => {
    identifyMixpanelUser(actorMember?.id)
  }, [actorMember?.id]) // Run when member.id changes

  return {
    authenticated: !!auth.data?.access_token,
    isSessionExpired,
    lastLoggedInAt,
    checkIfSessionIsExpired,
    updateLastLoggedIn,
  }
}
